import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  InputAdornment,
  Paper,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  deleteQuestionnaire,
  fetchAssignedQuestionnaireSections,
  fetchCustomQuestionnaireSections,
  fetchDraftCustomQuestionnaires,
  publishCustomQuestionnaire,
  updateQuestionnaire,
} from '@/lib/api/questionnaires';
import type { QuestionnaireSummaryCustomResponse } from '@/lib/api/questionnaires';
import { getCurrentUser } from '@/lib/api/auth';
import { useDebounce } from '@/hooks/useDebounce';
import type { QuestionnaireCardData } from '@/components/questionnaires/final-page/types';
import { getCardDescription, getCardTitle } from '@/components/questionnaires/final-page/types';
import { QuestionnaireCard } from '@/components/questionnaires/final-page/QuestionnaireCard';
import { QuestionnaireListRow } from '@/components/questionnaires/final-page/QuestionnaireListRow';
import { getQuestionnaireAssignmentMeta } from '@/components/questionnaires/final-page/meta';
import { extractErrorMessage, showErrorToast, showSuccessToast } from '@/utils/toast-utils';
import InlineFeedback, { type InlineFeedbackProps } from '@/components/shared/InlineFeedback';
import trashIcon from '@/assets/images/trash-bin-trash.svg';
import bookIcon from '@/assets/images/book.svg';
import bulbIcon from '@/assets/images/bulb.svg';

type Filter = 'all' | 'assigned' | 'draft';
type View = 'grid' | 'list';
type DialogFeedback = Pick<InlineFeedbackProps, 'variant' | 'title' | 'message'>;

const CUSTOM_QUESTIONNAIRE_HELP_COPY: Record<Filter, { title: string; message: string }> = {
  all: {
    title: 'Custom Questionnaires',
    message: 'Open Core Questionnaire and click the plus icon on a questionnaire. This creates a customizable copy that appears here.',
  },
  assigned: {
    title: 'Assigned Custom Questionnaires',
    message: 'This tab shows custom questionnaires that have already been assigned to clients. Open one to assign it to more clients or review responses.',
  },
  draft: {
    title: 'Draft Custom Questionnaires',
    message: 'Drafts are custom questionnaire copies that are not published yet. Publish a draft before assigning it to clients.',
  },
};

interface InvalidateCustomQuestionnaireQueriesOptions {
  queryClient: QueryClient;
  nutritionistId?: string;
  includeSummary?: boolean;
}

async function invalidateCustomQuestionnaireQueries({
  queryClient,
  nutritionistId,
  includeSummary = true,
}: InvalidateCustomQuestionnaireQueriesOptions) {
  const invalidatePromises = [
    queryClient.invalidateQueries({
      queryKey: ['questionnaireSections', 'final-page', 'custom', nutritionistId],
    }),
    queryClient.invalidateQueries({
      queryKey: ['questionnaireSections', 'final-page', 'assigned', 'custom', nutritionistId],
    }),
    queryClient.invalidateQueries({
      queryKey: ['questionnaireSections', 'final-page', 'draft', 'custom', nutritionistId],
    }),
  ];

  if (includeSummary) {
    invalidatePromises.push(
      queryClient.invalidateQueries({
        queryKey: ['questionnaireSummary', 'custom', nutritionistId],
      })
    );
  }

  await Promise.all(invalidatePromises);
}

interface DeleteQuestionnaireDialogProps {
  open: boolean;
  isSubmitting: boolean;
  feedback?: DialogFeedback | null;
  onClose: () => void;
  onConfirm: () => void;
}

function DeleteQuestionnaireDialog({
  open,
  isSubmitting,
  feedback,
  onClose,
  onConfirm,
}: DeleteQuestionnaireDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          px: { xs: 1.75, sm: 2.5 },
          py: { xs: 2, sm: 2.5 },
        },
      }}
    >
      <Box sx={{ display: 'grid', justifyItems: 'center', textAlign: 'center' }}>
        <Box
          sx={{
            width: { xs: 66, sm: 72 },
            height: { xs: 66, sm: 72 },
            borderRadius: '14px',
            bgcolor: '#fff1f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box component="img" src={trashIcon} alt="" sx={{ width: { xs: 34, sm: 38 }, height: { xs: 34, sm: 38 }, display: 'block' }} />
        </Box>

        <Typography
          sx={{
            mt: 1.75,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: { xs: '22px', sm: '24px' },
            lineHeight: { xs: '30px', sm: '32px' },
            color: '#0b1116',
            letterSpacing: '-0.03em',
          }}
        >
          Remove questionnaire?
        </Typography>

        <Typography
          sx={{
            mt: 0.75,
            maxWidth: 420,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: { xs: '12px', sm: '13px' },
            lineHeight: { xs: '18px', sm: '20px' },
            color: '#667085',
            letterSpacing: '-0.02em',
          }}
        >
          Are you sure you want to remove this questionnaire from your custom questionnaires?
        </Typography>

        {feedback ? (
          <Box sx={{ mt: 1.5, width: '100%' }}>
            <InlineFeedback
              variant={feedback.variant}
              title={feedback.title}
              message={feedback.message}
            />
          </Box>
        ) : null}

        <Box
          sx={{
            mt: 2.25,
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              height: 44,
              borderRadius: '16px',
              bgcolor: '#25a8da',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '13px' },
              lineHeight: 1.2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="outlined"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              height: 44,
              borderRadius: '16px',
              borderColor: '#d0d5dd',
              color: '#344054',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '12px', sm: '13px' },
              lineHeight: 1.2,
              textTransform: 'none',
              '&:hover': { borderColor: '#98a2b3', bgcolor: '#f8fafc' },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <CircularProgress size={18} thickness={6} sx={{ color: '#344054' }} />
                Removing...
              </Box>
            ) : (
              'Yes, remove'
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export interface EditQuestionnaireDialogProps {
  open: boolean;
  value: string;
  isSubmitting: boolean;
  feedback?: DialogFeedback | null;
  onChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}

export function EditQuestionnaireDialog({
  open,
  value,
  isSubmitting,
  feedback,
  onChange,
  onClose,
  onConfirm,
}: EditQuestionnaireDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          px: { xs: 1.75, sm: 2.5 },
          py: { xs: 2, sm: 2.5 },
        },
      }}
    >
      <Box sx={{ display: 'grid', gap: 1.5 }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: { xs: '20px', sm: '22px' },
            color: '#0b1116',
          }}
        >
          Edit questionnaire title
        </Typography>

        <TextField
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Questionnaire name"
          fullWidth
          autoFocus
          disabled={isSubmitting}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
            },
          }}
        />

        {feedback ? (
          <InlineFeedback
            variant={feedback.variant}
            title={feedback.title}
            message={feedback.message}
          />
        ) : null}

        <Box
          sx={{
            mt: 0.5,
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              height: 50,
              borderRadius: '14px',
              bgcolor: '#25a8da',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="outlined"
            onClick={onConfirm}
            disabled={isSubmitting || value.trim().length === 0}
            sx={{
              height: 50,
              borderRadius: '14px',
              borderColor: '#d0d5dd',
              color: '#344054',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': { borderColor: '#98a2b3', bgcolor: '#f8fafc' },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <CircularProgress size={20} thickness={6} sx={{ color: '#344054' }} />
                Saving...
              </Box>
            ) : (
              'Save'
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

interface DraftAssignConfirmDialogProps {
  open: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function DraftAssignConfirmDialog({
  open,
  isSubmitting,
  onClose,
  onConfirm,
}: DraftAssignConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 24px)', sm: 544 },
          maxWidth: 544,
          minHeight: { xs: 'auto', sm: 303 },
          borderRadius: '12px',
          m: { xs: 1.5, sm: 2 },
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          justifyItems: 'center',
          textAlign: 'center',
          gap: { xs: 2, sm: 3 },
          px: { xs: 2.5, sm: '30px' },
          py: { xs: 3, sm: '45px' },
        }}
      >
        <Box
          sx={{
            width: { xs: 58, sm: 62 },
            height: { xs: 58, sm: 62 },
            borderRadius: '14px',
            bgcolor: '#e7efff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box component="img" src={bookIcon} alt="" sx={{ width: 28, height: 28, display: 'block' }} />
        </Box>

        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: { xs: '24px', sm: '28px' },
            lineHeight: { xs: '32px', sm: '36px' },
            color: '#0b1116',
            letterSpacing: '-0.03em',
          }}
        >
          Assign Questionnaire?
        </Typography>

        <Typography
          sx={{
            maxWidth: 440,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: { xs: '13px', sm: '14px' },
            lineHeight: { xs: '20px', sm: '22px' },
            color: '#667085',
            letterSpacing: '-0.02em',
          }}
        >
          Assigning this questionnaire to clients will automatically publish it and move it out of Draft.
        </Typography>

        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              height: 44,
              borderRadius: '16px',
              borderColor: '#d0d5dd',
              color: '#344054',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '13px', sm: '14px' },
              lineHeight: 1.2,
              textTransform: 'none',
              '&:hover': { borderColor: '#98a2b3', bgcolor: '#f8fafc' },
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isSubmitting}
            sx={{
              height: 44,
              borderRadius: '16px',
              bgcolor: '#25a8da',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: { xs: '13px', sm: '14px' },
              lineHeight: 1.2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1498ca', boxShadow: 'none' },
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <CircularProgress size={18} thickness={6} sx={{ color: '#fff' }} />
                Continue
              </Box>
            ) : (
              'Continue'
            )}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

interface CustomQuestionnairesTabProps {
  viewMode: 'grid' | 'list';
  onViewModeChange: (v: 'grid' | 'list') => void;
  workflowFilter: Filter;
  onWorkflowFilterChange: (v: Filter) => void;
  /** Summary from parent (GET /questionnaires/summary?type=custom). When provided, metrics and filter counts use it. */
  summary?: QuestionnaireSummaryCustomResponse | null;
}

interface CustomListSectionProps {
  workflowFilter: Filter;
  viewMode: View;
  openDetail: (id: string) => void;
  openAssignment: (id: string) => void;
  openResponses: (id: string) => void;
  onEditQuestionnaire: (card: QuestionnaireCardData) => void;
  onDeleteQuestionnaire: (card: QuestionnaireCardData) => void;
  /** Raw search input from parent; debounced here so query key updates trigger API refetch. */
  searchQuery: string;
}

function CustomQuestionnairesListSection({
  workflowFilter,
  viewMode,
  openDetail,
  openAssignment,
  openResponses,
  onEditQuestionnaire,
  onDeleteQuestionnaire,
  searchQuery,
}: CustomListSectionProps) {
  const queryClient = useQueryClient();
  const user = getCurrentUser();
  const nutritionistId = user?.id;
  const debouncedSearch = useDebounce(searchQuery.trim(), 300);
  const [pendingAssignDraftQuestionnaireId, setPendingAssignDraftQuestionnaireId] = React.useState<string | null>(null);

  const publishQuestionnaireMutation = useMutation({
    mutationFn: (questionnaireId: string) => publishCustomQuestionnaire(questionnaireId),
    onSuccess: async (response) => {
      await invalidateCustomQuestionnaireQueries({
        queryClient,
        nutritionistId,
      });
      showSuccessToast('Questionnaire published', response.message);
    },
    onError: (mutationError) => {
      showErrorToast(mutationError, 'Unable to publish questionnaire');
    },
  });

  const query = useQuery({
    queryKey: ['questionnaireSections', 'final-page', 'custom', nutritionistId, debouncedSearch],
    queryFn: () => fetchCustomQuestionnaireSections(nutritionistId, debouncedSearch || undefined),
    enabled: Boolean(nutritionistId) && workflowFilter === 'all',
  });

  const assignedCustomQuery = useQuery({
    queryKey: ['questionnaireSections', 'final-page', 'assigned', 'custom', nutritionistId, debouncedSearch],
    queryFn: () => fetchAssignedQuestionnaireSections('custom', nutritionistId, debouncedSearch || undefined),
    enabled: Boolean(nutritionistId) && workflowFilter === 'assigned',
  });

  const draftCustomQuery = useQuery({
    queryKey: ['questionnaireSections', 'final-page', 'draft', 'custom', nutritionistId],
    queryFn: () => fetchDraftCustomQuestionnaires(nutritionistId),
    enabled: Boolean(nutritionistId) && workflowFilter === 'draft',
  });

  const cards = React.useMemo((): QuestionnaireCardData[] => {
    const sections = query.data?.questionnaires ?? [];
    return sections.map((s) => ({
      ...s,
      type: 'custom',
      status: s.status === 'draft' ? ('draft' as const) : ('customized' as const),
    }));
  }, [query.data]);

  const assignedCards = React.useMemo((): QuestionnaireCardData[] => {
    const sections = assignedCustomQuery.data?.questionnaires ?? [];
    return sections.map((s) => ({
      ...s,
      type: 'custom',
      status: s.status === 'draft' ? ('draft' as const) : ('customized' as const),
    }));
  }, [assignedCustomQuery.data]);

  const draftCards = React.useMemo((): QuestionnaireCardData[] => {
    const sections = draftCustomQuery.data?.questionnaires ?? [];
    return sections
      .filter((s) => s.status === 'draft')
      .map((s) => ({
        ...s,
        type: 'custom',
        status: 'draft' as const,
      }));
  }, [draftCustomQuery.data]);

  const localAssignedCards = React.useMemo(
    () => cards.filter((c) => getQuestionnaireAssignmentMeta(c).isAssigned),
    [cards]
  );

  const localDraftCards = React.useMemo(
    () => cards.filter((card) => card.status === 'draft'),
    [cards]
  );

  const visibleCards = React.useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const sourceCards =
      workflowFilter === 'all'
        ? cards
        : workflowFilter === 'assigned'
          ? assignedCustomQuery.data
            ? assignedCards
            : localAssignedCards
          : draftCustomQuery.data
            ? draftCards
            : localDraftCards;

    if (q.length === 0) return sourceCards;
    return sourceCards.filter((card) => {
      const title = getCardTitle(card);
      const description = getCardDescription(card);
      return (
        title.toLowerCase().includes(q) ||
        (description && description.toLowerCase().includes(q))
      );
    });
  }, [
    assignedCards,
    assignedCustomQuery.data,
    cards,
    debouncedSearch,
    draftCards,
    draftCustomQuery.data,
    localAssignedCards,
    localDraftCards,
    workflowFilter,
  ]);

  const publishDraftQuestionnaire = React.useCallback(
    async (questionnaireId: string, intent: 'publish' | 'assign') => {
      if (publishQuestionnaireMutation.isPending) return;

      try {
        await publishQuestionnaireMutation.mutateAsync(questionnaireId);

        if (intent === 'assign') {
          openAssignment(questionnaireId);
        }
      } catch {
        // toast handled in mutation onError
      }
    },
    [openAssignment, publishQuestionnaireMutation]
  );

  const handleSecondaryAction = React.useCallback(
    (card: QuestionnaireCardData) => {
      if (card.status === 'draft') {
        void publishDraftQuestionnaire(card.id, 'publish');
        return;
      }

      openResponses(card.id);
    },
    [openResponses, publishDraftQuestionnaire]
  );

  const handleAssignAction = React.useCallback(
    (card: QuestionnaireCardData) => {
      if (card.status === 'draft') {
        setPendingAssignDraftQuestionnaireId(card.id);
        return;
      }

      openAssignment(card.id);
    },
    [openAssignment]
  );

  const handleCloseAssignDraftDialog = React.useCallback(() => {
    if (publishQuestionnaireMutation.isPending) return;
    setPendingAssignDraftQuestionnaireId(null);
  }, [publishQuestionnaireMutation.isPending]);

  const handleConfirmAssignDraft = React.useCallback(() => {
    if (!pendingAssignDraftQuestionnaireId) return;
    const questionnaireId = pendingAssignDraftQuestionnaireId;
    setPendingAssignDraftQuestionnaireId(null);
    void publishDraftQuestionnaire(questionnaireId, 'assign');
  }, [pendingAssignDraftQuestionnaireId, publishDraftQuestionnaire]);

  if (query.isError) {
    return (
      <Paper
        elevation={0}
        sx={{ mt: 3, borderRadius: '18px', border: '1px solid #f3d089', bgcolor: '#fff8e8', px: 2, py: 1.5 }}
      >
        <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#8a5a00' }}>
          Custom questionnaire data could not be loaded.
        </Typography>
      </Paper>
    );
  }

  if (workflowFilter === 'all' && query.isLoading) {
    return (
      <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={356} sx={{ borderRadius: '28px' }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {workflowFilter === 'assigned' && assignedCustomQuery.isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: 2, sm: 3 },
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={356} sx={{ borderRadius: '28px' }} />
          ))}
        </Box>
      ) : workflowFilter === 'draft' && draftCustomQuery.isLoading ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: 2, sm: 3 },
          }}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={356} sx={{ borderRadius: '28px' }} />
          ))}
        </Box>
      ) : visibleCards.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '24px',
            border: '1px dashed #dbe6eb',
            bgcolor: '#fbfdff',
            px: { xs: 2, sm: 3 },
            py: { xs: 4, sm: 7 },
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: { xs: '20px', sm: '24px' }, color: '#0f172a' }}
          >
            {workflowFilter === 'draft' ? 'No draft questionnaires' : 'No custom questionnaires'}
          </Typography>
        </Paper>
      ) : viewMode === 'grid' ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: 2, sm: 3 },
          }}
        >
          {visibleCards.map((card) => {
            const isPublishingDraftCard =
              card.status === 'draft' &&
              publishQuestionnaireMutation.isPending &&
              publishQuestionnaireMutation.variables === card.id;

            return (
              <QuestionnaireCard
                key={card.id}
                card={card}
                onPreview={() => openDetail(card.id)}
                onResponses={() => handleSecondaryAction(card)}
                onAssign={() => handleAssignAction(card)}
                responsesLabel={card.status === 'draft' ? (isPublishingDraftCard ? 'Publishing...' : 'Publish') : 'Responses'}
                disableResponses={card.status === 'draft' ? isPublishingDraftCard : undefined}
                disableAssign={card.status === 'draft' ? isPublishingDraftCard : undefined}
                onEdit={() => onEditQuestionnaire(card)}
                onDelete={() => onDeleteQuestionnaire(card)}
              />
            );
          })}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 2.25 }}>
          {visibleCards.map((card) => {
            const isPublishingDraftCard =
              card.status === 'draft' &&
              publishQuestionnaireMutation.isPending &&
              publishQuestionnaireMutation.variables === card.id;

            return (
              <QuestionnaireListRow
                key={card.id}
                card={card}
                onPreview={() => openDetail(card.id)}
                onResponses={() => handleSecondaryAction(card)}
                onAssign={() => handleAssignAction(card)}
                showDescription={false}
                responsesLabel={card.status === 'draft' ? (isPublishingDraftCard ? 'Publishing...' : 'Publish') : 'Responses'}
                disableResponses={card.status === 'draft' ? isPublishingDraftCard : undefined}
                disableAssign={card.status === 'draft' ? isPublishingDraftCard : undefined}
                onEdit={() => onEditQuestionnaire(card)}
                onDelete={() => onDeleteQuestionnaire(card)}
              />
            );
          })}
        </Box>
      )}

      <DraftAssignConfirmDialog
        open={Boolean(pendingAssignDraftQuestionnaireId)}
        isSubmitting={publishQuestionnaireMutation.isPending}
        onClose={handleCloseAssignDraftDialog}
        onConfirm={handleConfirmAssignDraft}
      />
    </Box>
  );
}

export function CustomQuestionnairesTab({
  workflowFilter,
  onWorkflowFilterChange,
  summary: summaryFromParent,
}: CustomQuestionnairesTabProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = getCurrentUser();
  const nutritionistId = user?.id;
  const assignedCustomTotalQuery = useQuery({
    queryKey: ['questionnaireSections', 'final-page', 'assigned', 'custom', nutritionistId, ''],
    queryFn: () => fetchAssignedQuestionnaireSections('custom', nutritionistId),
    enabled: Boolean(nutritionistId),
  });
  const [pendingEditQuestionnaire, setPendingEditQuestionnaire] = React.useState<QuestionnaireCardData | null>(null);
  const [editQuestionnaireName, setEditQuestionnaireName] = React.useState('');
  const [pendingDeleteQuestionnaire, setPendingDeleteQuestionnaire] = React.useState<QuestionnaireCardData | null>(null);
  const [editDialogFeedback, setEditDialogFeedback] = React.useState<DialogFeedback | null>(null);
  const [deleteDialogFeedback, setDeleteDialogFeedback] = React.useState<DialogFeedback | null>(null);

  const deleteQuestionnaireMutation = useMutation({
    mutationFn: (questionnaireId: string) => deleteQuestionnaire(questionnaireId),
    onSuccess: async (response) => {
      setPendingDeleteQuestionnaire(null);
      setDeleteDialogFeedback(null);
      await invalidateCustomQuestionnaireQueries({
        queryClient,
        nutritionistId,
      });
      showSuccessToast('Questionnaire deleted', response.message);
    },
    onError: (mutationError) => {
      setDeleteDialogFeedback({
        variant: 'error',
        title: 'Unable to delete questionnaire',
        message: extractErrorMessage(mutationError),
      });
    },
  });
  const updateQuestionnaireMutation = useMutation({
    mutationFn: ({ questionnaireId, name }: { questionnaireId: string; name: string }) =>
      updateQuestionnaire(questionnaireId, { name }),
    onSuccess: async (response) => {
      setPendingEditQuestionnaire(null);
      setEditQuestionnaireName('');
      setEditDialogFeedback(null);
      await invalidateCustomQuestionnaireQueries({
        queryClient,
        nutritionistId,
        includeSummary: false,
      });
      showSuccessToast('Questionnaire updated', response.message);
    },
    onError: (mutationError) => {
      setEditDialogFeedback({
        variant: 'error',
        title: 'Unable to update questionnaire',
        message: extractErrorMessage(mutationError),
      });
    },
  });

  const filterCounts = React.useMemo(() => {
    const s = summaryFromParent;
    const assignedCount = assignedCustomTotalQuery.data?.total ?? 0;
    if (s && s.type === 'custom') {
      return {
        all: s.customQuestionnairesCount ?? 0,
        assigned: assignedCount,
        draft: s.draftCount ?? 0,
      };
    }
    return { all: 0, assigned: assignedCount, draft: 0 };
  }, [assignedCustomTotalQuery.data?.total, summaryFromParent]);

  const openDetail = React.useCallback(
    (id: string) =>
      navigate(`/questionnaires/${id}`, {
        state: {
          isPreviewMode: true,
          questionnairesBackPath: `${location.pathname}${location.search}`,
        },
      }),
    [location.pathname, location.search, navigate]
  );
  const openAssignment = React.useCallback((id: string) => navigate(`/questionnaires/${id}/assign`), [navigate]);
  const openResponses = React.useCallback((id: string) => navigate(`/questionnaires/${id}/responses`), [navigate]);
  const handleEditQuestionnaire = React.useCallback((card: QuestionnaireCardData) => {
    setEditDialogFeedback(null);
    setPendingEditQuestionnaire(card);
    setEditQuestionnaireName(getCardTitle(card));
  }, []);
  const handleDeleteQuestionnaire = React.useCallback(
    (card: QuestionnaireCardData) => {
      setDeleteDialogFeedback(null);
      setPendingDeleteQuestionnaire(card);
    },
    []
  );
  const handleCloseDeleteQuestionnaireDialog = React.useCallback(() => {
    setDeleteDialogFeedback(null);
    setPendingDeleteQuestionnaire(null);
  }, []);
  const handleConfirmDeleteQuestionnaire = React.useCallback(() => {
    if (!pendingDeleteQuestionnaire) return;
    deleteQuestionnaireMutation.mutate(pendingDeleteQuestionnaire.id);
  }, [deleteQuestionnaireMutation, pendingDeleteQuestionnaire]);
  const handleCloseEditQuestionnaireDialog = React.useCallback(() => {
    setEditDialogFeedback(null);
    setPendingEditQuestionnaire(null);
    setEditQuestionnaireName('');
  }, []);
  const handleConfirmEditQuestionnaire = React.useCallback(() => {
    if (!pendingEditQuestionnaire) return;
    updateQuestionnaireMutation.mutate({
      questionnaireId: pendingEditQuestionnaire.id,
      name: editQuestionnaireName.trim(),
    });
  }, [editQuestionnaireName, pendingEditQuestionnaire, updateQuestionnaireMutation]);

  const [searchQuery, setSearchQuery] = React.useState('');
  const helpCopy = CUSTOM_QUESTIONNAIRE_HELP_COPY[workflowFilter];

  return (
    <Box sx={{ mt: 3, display: 'grid', gap: 3 }}>
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', lg: 'center' },
            gap: 2,
            borderBottom: { xs: 'none', lg: '1px solid #e2e8f0' },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0,
              alignItems: 'center',
              borderBottom: { xs: '1px solid #e2e8f0', lg: 'none' },
              flex: { xs: '0 0 auto', lg: '1 1 auto' },
              minWidth: 0,
            }}
          >
            {([{ key: 'all' as const, label: 'All' }, { key: 'assigned' as const, label: 'Assigned' }, { key: 'draft' as const, label: 'Draft' }] as const).map(
              (item) => {
                const isActive = workflowFilter === item.key;
                return (
                  <Button
                    key={item.key}
                    variant="text"
                    onClick={() => onWorkflowFilterChange(item.key)}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '14px',
                      color: isActive ? '#1699c8' : '#667085',
                      bgcolor: 'transparent',
                      border: 'none',
                      borderBottom: isActive ? '2px solid #1699c8' : '2px solid transparent',
                      py: 1,
                      px: { xs: 1, sm: 1.5 },
                      mr: { xs: 1, sm: 2 },
                      marginBottom: '-1px',
                      borderRadius: 0,
                      minHeight: 44,
                      '&:hover': { bgcolor: 'transparent', color: isActive ? '#1699c8' : '#475467' },
                    }}
                  >
                    <Box component="span" sx={{ mr: 0.75 }}>{item.label}</Box>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 24,
                        height: 22,
                        px: 1,
                        borderRadius: '999px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '13px', sm: '14px' },
                        bgcolor: isActive ? '#47a9d1' : '#e2e8f0',
                        color: isActive ? '#fff' : '#475467',
                      }}
                    >
                      {filterCounts[item.key]}
                    </Box>
                  </Button>
                );
              }
            )}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5 }}>
            <TextField
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search"
              size="small"
              sx={{
                width: { xs: '100%', sm: 180, md: 200 },
                maxWidth: '100%',
                '& .MuiOutlinedInput-root': {
                  height: { xs: 32, sm: 34 },
                  borderRadius: '999px',
                  bgcolor: '#f6f7f9',
                  pl: { xs: 0.25, sm: 0.5 },
                  pr: { xs: 0.375, sm: 0.5 },
                  '& fieldset': { borderColor: '#eaecf0' },
                  '&:hover fieldset': { borderColor: '#dde1e7' },
                  '&.Mui-focused fieldset': { borderColor: '#d5dae2' },
                },
                '& .MuiInputBase-input': {
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '12px', sm: '13px' },
                  color: '#8c9199',
                  py: 0,
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#a8acb3',
                  opacity: 1,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: '#b0b3b8', fontSize: { xs: 18, sm: 20 } }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box
                      component="span"
                      sx={{
                        width: { xs: 20, sm: 24 },
                        height: { xs: 18, sm: 22 },
                        borderRadius: { xs: '7px', sm: '8px' },
                        border: '1px solid #d0d2d6',
                        color: '#b4b6bb',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '14px' },
                        lineHeight: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      /
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <Paper
          elevation={0}
          sx={{
            mt: 3,
            width: '100%',
            minHeight: 76,
            borderRadius: '8px',
            border: '1px solid #4ea7ef',
            bgcolor: 'transparent',
            p: { xs: 1.5, sm: '12px' },
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'flex-start',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 'none',
              mx: 0,
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: '18px' },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={bulbIcon}
                alt=""
                sx={{ width: 20, height: 20, display: 'block' }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '15px',
                  letterSpacing: '0%',
                  color: '#101828',
                }}
              >
                {helpCopy.title}
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '145%',
                  letterSpacing: '0%',
                  color: '#667085',
                  overflowWrap: 'anywhere',
                }}
              >
                {helpCopy.message}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <CustomQuestionnairesListSection
          workflowFilter={workflowFilter}
          viewMode="list"
          openDetail={openDetail}
          openAssignment={openAssignment}
          openResponses={openResponses}
          onEditQuestionnaire={handleEditQuestionnaire}
          onDeleteQuestionnaire={handleDeleteQuestionnaire}
          searchQuery={searchQuery}
        />
      </Box>

      <EditQuestionnaireDialog
        open={Boolean(pendingEditQuestionnaire)}
        value={editQuestionnaireName}
        isSubmitting={updateQuestionnaireMutation.isPending}
        feedback={editDialogFeedback}
        onChange={setEditQuestionnaireName}
        onClose={handleCloseEditQuestionnaireDialog}
        onConfirm={handleConfirmEditQuestionnaire}
      />

      <DeleteQuestionnaireDialog
        open={Boolean(pendingDeleteQuestionnaire)}
        isSubmitting={deleteQuestionnaireMutation.isPending}
        feedback={deleteDialogFeedback}
        onClose={handleCloseDeleteQuestionnaireDialog}
        onConfirm={handleConfirmDeleteQuestionnaire}
      />
    </Box>
  );
}
